import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Store, Gamepad2, Tv, GraduationCap, Joystick, Heart, Lock, Code2, Mail, Linkedin, Instagram, ArrowUp, ArrowDown, ArrowLeft, ArrowRight } from "lucide-react";
import { useKeyboardMovement } from "@/hooks/useKeyboardMovement";
import { getMemories, getGameMemories, getSchoolMemories, getTvMemories } from "@/lib/memories";
import { Avatar } from "@/components/game/Avatar";
import { AmbientMusic } from "@/components/AmbientMusic";
import { MoveHint } from "@/components/game/MoveHint";
import { InteractPrompt } from "@/components/game/InteractPrompt";
import { useIsTouch } from "@/hooks/useIsTouch";
import { Modal } from "@/components/Modal";
import { GameArcade } from "@/components/games/GameArcade";
import { TimeTravelIntro } from "@/components/TimeTravelIntro";

const BOUNDS = { minX: 5, maxX: 95, minY: 26, maxY: 90 };
const THRESHOLD = 11;
const START = { x: 50, y: 86 };

const BUILDINGS = [
  { id: "kirana", title: "Kirana Store", to: "/kirana", img: "/home/facade_kirana.jpg", icon: Store, live: true, color: "#C84B31", x: 20, y: 38 },
  { id: "tv", title: "TV & Entertainment", to: "/tv", img: "/home/facade_tv.jpg", icon: Tv, live: true, color: "#457B9D", x: 80, y: 38 },
  { id: "games", title: "Mohalla Games", to: "/games", img: "/home/facade_games.jpg", icon: Gamepad2, live: true, color: "#2D6A4F", x: 20, y: 62 },
  { id: "parlor", title: "Game Room", to: "/parlor", img: "/home/facade_parlor.jpg", icon: Joystick, live: true, color: "#7C3AED", x: 80, y: 62 },
  { id: "school", title: "School Days", to: "/school", img: "/home/facade_school.jpg", icon: GraduationCap, live: true, color: "#E76F51", x: 50, y: 80 },
];

const Prompt = InteractPrompt;

const Building = ({ b, active, onClick }) => {
  const Icon = b.icon;
  return (
    <div className="absolute z-20" style={{ left: `${b.x}%`, top: `${b.y}%`, transform: "translate(-50%, -50%)" }}>
      {active && <Prompt />}
      <button
        data-testid={`world-${b.id}`}
        onClick={onClick}
        className={`group relative flex flex-col items-center rounded-xl bg-black/40 px-1 pb-1.5 pt-1 ring-2 ring-white/15 backdrop-blur-[2px] transition-transform duration-150 hover:scale-[1.05] sm:px-2 sm:pb-2 ${active ? "scale-[1.05] ring-kirana-gold" : ""}`}
      >
        {/* signboard */}
        <div className="z-10 -mb-1 rounded-md border-2 border-kirana-brown bg-kirana-cream px-2 py-0.5 hard-shadow-sm">
          <span className="font-pixel block max-w-[78px] text-center text-[8px] font-bold leading-[1.5] text-kirana-brown max-[359px]:max-w-[62px] max-[359px]:text-[7px] sm:max-w-[120px] sm:text-[9px]">{b.title}</span>
        </div>
        {/* awning */}
        <div className="h-2 w-[78px] rounded-t-sm max-[359px]:w-[66px] sm:w-[128px]" style={{ background: `repeating-linear-gradient(90deg, ${b.color} 0 12px, #FDF6E3 12px 24px)` }} />
        {/* facade */}
        <div className={`relative h-[68px] w-[78px] overflow-hidden border-[3px] border-kirana-brown max-[359px]:h-[58px] max-[359px]:w-[66px] sm:h-24 sm:w-32 ${active ? "zone-glow" : "hard-shadow"}`} style={{ background: b.color }}>
          {b.img ? (
            <img src={b.img} alt={b.title} className="pixelated h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <Icon className="h-10 w-10 text-white/85" strokeWidth={1.6} />
            </div>
          )}
          {b.live ? (
            <span className="absolute left-1 top-1 rounded border border-kirana-brown bg-kirana-green px-1.5 py-0.5 text-[9px] font-bold uppercase text-white">Open</span>
          ) : (
            <span className="absolute left-1 top-1 flex items-center gap-0.5 rounded border border-kirana-brown bg-kirana-brown px-1 py-0.5 text-[9px] font-bold uppercase text-kirana-cream">
              <Lock className="h-2.5 w-2.5" /> Soon
            </span>
          )}
        </div>
        {/* steps */}
        <div className="h-1.5 w-[84px] rounded-b bg-kirana-brown/60 max-[359px]:w-[72px] sm:w-36" />
      </button>
    </div>
  );
};

export default function WorldHub() {
  const navigate = useNavigate();
  const [totalMem] = useState(getMemories().length + getGameMemories().length + getSchoolMemories().length + getTvMemories().length);
  const [devOpen, setDevOpen] = useState(false);
  const [arcadeOpen, setArcadeOpen] = useState(false);
  const isTouch = useIsTouch();
  const anyOverlay = devOpen || arcadeOpen;
  const { pos, setPos, facing, moving, moveTo } = useKeyboardMovement({ initial: START, bounds: BOUNDS, enabled: !anyOverlay });

  const tapToWalk = (e) => {
    if (anyOverlay) return;
    const r = e.currentTarget.getBoundingClientRect();
    moveTo(((e.clientX - r.left) / r.width) * 100, ((e.clientY - r.top) / r.height) * 100);
  };

  const active = useMemo(() => {
    let best = null;
    let bestD = THRESHOLD;
    for (const b of BUILDINGS) {
      const d = Math.hypot(b.x - pos.x, b.y - pos.y);
      if (d < bestD) { bestD = d; best = b; }
    }
    return best;
  }, [pos]);

  const enter = (b) => {
    if (!b) return;
    if (b.live) navigate(b.to);
    else toast(`${b.title} — coming soon! 🚧`);
  };

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Enter" && active && !anyOverlay) { e.preventDefault(); enter(active); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, anyOverlay]);

  // touch dpad
  const holdRef = useRef(null);
  const nudge = (dx, dy) =>
    setPos((p) => ({ x: Math.max(BOUNDS.minX, Math.min(BOUNDS.maxX, p.x + dx)), y: Math.max(BOUNDS.minY, Math.min(BOUNDS.maxY, p.y + dy)) }));
  const [showIntro, setShowIntro] = useState(() => !sessionStorage.getItem("tt_intro_seen"));
  const startHold = (dx, dy) => { nudge(dx, dy); holdRef.current = setInterval(() => nudge(dx, dy), 40); };
  const stopHold = () => holdRef.current && clearInterval(holdRef.current);

  return (
    <div className="min-h-screen bg-background paper-grain">
      {showIntro && <TimeTravelIntro onDone={() => { sessionStorage.setItem("tt_intro_seen", "1"); setShowIntro(false); }} />}
      <AmbientMusic src="/audio/nostalgic.mp3" />
      <header className="sticky top-0 z-40 border-b-[3px] border-kirana-brown bg-kirana-brown text-kirana-cream">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-x-3 gap-y-2 px-4 py-2.5">
          <div className="min-w-0">
            <h1 className="font-pixel text-[11px] font-bold leading-tight sm:text-base">Nostalgic World</h1>
            <p className="mt-1 text-[11px] text-kirana-cream/80">Ready to take a ride to the past — walk up to a place and press ⏎ to step inside ✨</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {totalMem > 0 && (
              <div data-testid="world-memory-count" className="flex items-center gap-1.5 rounded-md border-2 border-kirana-cream bg-black/25 px-2.5 py-1 text-xs font-bold sm:px-3 sm:py-1.5 sm:text-sm">
                <Heart className="h-4 w-4 fill-kirana-cream" /> {totalMem} <span className="hidden sm:inline">memories</span>
              </div>
            )}
            <button data-testid="open-developer" onClick={() => setDevOpen(true)}
              className="flex shrink-0 items-center gap-1.5 rounded-md border-2 border-kirana-cream bg-black/25 px-2.5 py-1 text-xs font-bold transition-colors hover:bg-black/40 sm:px-3 sm:py-1.5 sm:text-sm">
              <Code2 className="h-4 w-4 shrink-0" /> <span className="whitespace-nowrap">About / Say Hi</span>
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-3 py-4">
        <div className="mb-2 flex items-center justify-end">
          <MoveHint verb="enter" />
        </div>

        <div data-testid="world-area" className="play-surface relative h-[72vh] min-h-[500px] w-full overflow-hidden rounded-xl border-[4px] border-kirana-brown hard-shadow">
          {/* illustrated nostalgic-world backdrop */}
          <div className="pointer-events-none absolute inset-0 z-10 overflow-hidden">
            <img src="/home/world_bg.jpg" alt="" className="pixelated h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-black/40" />
            {/* CRT scanlines for the retro-game feel */}
            <div className="crt-overlay absolute inset-0" />
            {/* welcome banner */}
            <div className="absolute left-1/2 top-[3%] -translate-x-1/2 rounded-md border-2 border-kirana-brown bg-kirana-red px-3 py-1.5 text-center hard-shadow-sm">
              <span className="font-pixel text-[8px] text-kirana-cream sm:text-[10px]">PRESS ⏎ TO ENTER</span>
            </div>
          </div>

          {BUILDINGS.map((b) => (
            <Building key={b.id} b={b} active={active?.id === b.id} onClick={() => enter(b)} />
          ))}

          {/* tap-to-move floor — below buildings (z-20), above backdrop (z-10) */}
          <div data-testid="floor-tap" className="absolute inset-0 z-[15] cursor-pointer" onClick={tapToWalk} />

          <Avatar pos={pos} facing={facing} moving={moving} />
        </div>

        {/* touch controls — below the scene so they never cover a card */}
        <div className={`touch-pad mx-auto mt-3 w-max grid-cols-3 gap-1.5 ${isTouch ? "grid" : "hidden"}`}>
          <span />
          <DPad onDown={() => startHold(0, -1.5)} onUp={stopHold} icon={ArrowUp} tid="dpad-up" />
          <span />
          <DPad onDown={() => startHold(-1.5, 0)} onUp={stopHold} icon={ArrowLeft} tid="dpad-left" />
          <DPad onDown={() => active && enter(active)} onUp={() => {}} label="⏎" tid="dpad-enter" />
          <DPad onDown={() => startHold(1.5, 0)} onUp={stopHold} icon={ArrowRight} tid="dpad-right" />
          <span />
          <DPad onDown={() => startHold(0, 1.5)} onUp={stopHold} icon={ArrowDown} tid="dpad-down" />
          <span />
        </div>
      </main>

      {arcadeOpen && <GameArcade open={arcadeOpen} onClose={() => setArcadeOpen(false)} />}

      {devOpen && (
        <Modal open={devOpen} onClose={() => setDevOpen(false)} testId="developer-modal" maxWidth="max-w-md">
          <div className="p-6 sm:p-8">
            <div className="mb-3 flex items-center gap-2">
              <span className="flex h-11 w-11 items-center justify-center rounded-full border-[3px] border-kirana-brown bg-kirana-gold">
                <Code2 className="h-5 w-5 text-kirana-brown" />
              </span>
              <div>
                <h2 className="font-signboard text-2xl font-bold leading-tight text-kirana-brown">Developer · About & Suggestions</h2>
                <p className="text-xs text-kirana-brown/70">the one who bunked class to build this 😄 — say hi or drop a suggestion!</p>
              </div>
            </div>
            <p data-testid="dev-praise" className="rounded-md border-2 border-dashed border-kirana-border bg-[repeating-linear-gradient(#FDF6E3,#FDF6E3_22px,#cdd4e6_23px)] p-3 text-sm italic text-kirana-brown">
              "Let's just pretend I've written my praise here. Truly the eighth wonder of the world. A visionary. A legend. Please clap. 👏 (Not that anyone asked, or cares.)"
            </p>
            <p className="mt-3 text-center text-xs font-bold uppercase tracking-wide text-kirana-brown/60">Got an idea or feedback? Reach out ↓</p>
            <div className="mt-2 flex flex-col gap-2">
              <a data-testid="dev-email" href="mailto:saiyediqqbal@gmail.com"
                className="flex items-center gap-2 rounded-md border-2 border-kirana-brown bg-kirana-cream px-3 py-2 text-sm font-bold text-kirana-brown transition-transform hover:scale-[1.02] hard-shadow-sm">
                <Mail className="h-4 w-4 text-kirana-red" /> saiyediqqbal@gmail.com
              </a>
              <a data-testid="dev-linkedin" href="https://www.linkedin.com/in/saiyed-ali-iqbal-59ab4a1a6/" target="_blank" rel="noreferrer"
                className="flex items-center gap-2 rounded-md border-2 border-kirana-brown bg-[#0A66C2] px-3 py-2 text-sm font-bold text-white transition-transform hover:scale-[1.02] hard-shadow-sm">
                <Linkedin className="h-4 w-4" /> LinkedIn Profile
              </a>
              <a data-testid="dev-instagram" href="https://www.instagram.com/saiyedali070" target="_blank" rel="noreferrer"
                className="flex items-center gap-2 rounded-md border-2 border-kirana-brown bg-gradient-to-r from-[#833AB4] via-[#E1306C] to-[#F77737] px-3 py-2 text-sm font-bold text-white transition-transform hover:scale-[1.02] hard-shadow-sm">
                <Instagram className="h-4 w-4" /> @saiyedali070
              </a>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

const DPad = ({ onDown, onUp, icon: Icon, label, tid }) => (
  <button
    data-testid={tid}
    onPointerDown={(e) => { e.preventDefault(); onDown(); }}
    onPointerUp={onUp}
    onPointerLeave={onUp}
    onContextMenu={(e) => e.preventDefault()}
    className="flex h-11 w-11 items-center justify-center rounded-md border-2 border-kirana-brown bg-kirana-cream/90 text-kirana-brown hard-shadow-sm"
  >
    {Icon ? <Icon className="h-5 w-5" strokeWidth={3} /> : <span className="font-bold">{label}</span>}
  </button>
);
